import React, { useEffect, useState } from 'react'

import type { DragEndEvent } from '@dnd-kit/core'
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable'
import { AddButton, Loading } from '@fourthwall/components'
import { arrayMove } from '@fourthwall/utils/dist/array'

import { useTiersContext } from '../../hooks/useTiersContext'
import { PerksProvider } from '../../providers/PerksProvider'
import { isPaidTier } from '../../utils'
import { TierBox } from '../TierBox'
import * as S from './styled'

export const Tiers = () => {
  const { tiers, addTier, sortTier, sortFreeTier, isLoading } = useTiersContext()

  const [innerTiers, setInnerTiers] = useState(tiers)

  useEffect(() => {
    setInnerTiers(tiers)
  }, [tiers])

  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  if (!innerTiers) return <Loading isLoading />

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    const oldIndex = innerTiers.findIndex((tier) => tier.id === active.id)
    const newIndex = innerTiers.findIndex((tier) => tier.id === over?.id)
    const tier = innerTiers.find((tier) => tier.id === active.id)
    const newTier = innerTiers.find((tier) => tier.id === over?.id)

    if (!tier) return
    if (!newTier) return
    if (newIndex < 0) return
    if (newIndex === oldIndex) return

    setInnerTiers(arrayMove([...innerTiers], oldIndex, newIndex))

    if (isPaidTier(tier)) {
      sortTier(tier, newTier.position)
    } else {
      sortFreeTier(newTier.position)
    }
  }

  const renderAddTierButton = () => {
    if (!addTier) return null

    return (
      <S.AddTierButton type="button" onClick={addTier}>
        <AddButton label="Add a new tier" />
      </S.AddTierButton>
    )
  }

  const renderContent = () => {
    if (!innerTiers.length) {
      return <S.AddTierButtonContainer>{renderAddTierButton()}</S.AddTierButtonContainer>
    }

    return (
      <S.Grid count={innerTiers.length}>
        {innerTiers.map((tier) => (
          <PerksProvider tierId={tier.id} key={tier.id}>
            <TierBox tier={tier} />
          </PerksProvider>
        ))}
        {renderAddTierButton()}
      </S.Grid>
    )
  }

  return (
    <Loading isLoading={isLoading} size="medium">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={innerTiers} strategy={rectSortingStrategy}>
          {renderContent()}
        </SortableContext>
      </DndContext>
    </Loading>
  )
}
